import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, ChevronDown } from "lucide-react";
import { TEXT_SECTIONS, getDefault } from "../lib/textSchema";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTexts({ token }) {
  const [overrides, setOverrides] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState("hero");

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then(({ data }) => setOverrides(data?.overrides || {}))
      .catch(() => setOverrides({}));
  }, []);

  if (overrides === null) {
    return <div className="font-mono text-xs text-neutral-500 py-10">Loading texts…</div>;
  }

  const setField = (path, lang, value) => {
    setOverrides((o) => ({ ...o, [`${path}.${lang}`]: value }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(
        Object.entries(overrides).filter(([, v]) => String(v).trim() !== "")
      );
      const { data } = await axios.put(
        `${API}/content`,
        { overrides: clean },
        { headers: { "X-Admin-Token": token } }
      );
      setOverrides(data.overrides || {});
      setDirty(false);
      toast.success("Texts published — refresh the site to see them");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const editedCount = Object.values(overrides).filter((v) => String(v).trim()).length;

  return (
    <div data-testid="admin-texts-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 sticky top-[89px] bg-[#0a0a0a]/95 backdrop-blur py-3 z-10 border-b border-[#1a1a1a]">
        <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          **text** = white highlight · blank = original text · {editedCount} customized
        </p>
        <button
          data-testid="texts-save-button"
          onClick={save}
          disabled={!dirty || saving}
          className={`inline-flex items-center gap-2 px-5 py-2 text-[10px] uppercase tracking-widest font-medium ${
            dirty ? "bg-[#ff4500] text-black" : "bg-[#1a1a1a] text-neutral-600 cursor-not-allowed"
          }`}
        >
          <Save size={12} /> {saving ? "Publishing…" : dirty ? "Publish texts" : "Published"}
        </button>
      </div>

      <div className="space-y-3">
        {TEXT_SECTIONS.map((sec) => (
          <div key={sec.id} className="border border-[#262626] bg-[#0f0f0f]">
            <button
              type="button"
              data-testid={`texts-section-${sec.id}`}
              onClick={() => setOpen(open === sec.id ? "" : sec.id)}
              className="w-full flex items-center justify-between px-5 py-4 hover:text-[#ff4500] transition-colors"
            >
              <span className="font-serif text-xl font-light">{sec.label}</span>
              <ChevronDown
                size={16}
                className={`transition-transform duration-300 ${open === sec.id ? "rotate-180" : ""}`}
              />
            </button>
            {open === sec.id && (
              <div className="px-5 pb-5 pt-4 space-y-5 border-t border-[#1a1a1a]">
                {sec.fields.map((f) => (
                  <div key={f.path}>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 mb-1.5">
                      {f.label || f.path}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {["en", "pt"].map((lang) => (
                        <label key={lang} className="flex flex-col">
                          <span className="font-mono text-[8px] uppercase tracking-widest text-[#ff4500] mb-1">
                            {lang === "en" ? "English" : "Português"}
                          </span>
                          {f.area ? (
                            <textarea
                              rows={3}
                              data-testid={`text-${f.path}-${lang}`}
                              value={overrides[`${f.path}.${lang}`] ?? ""}
                              placeholder={getDefault(f.path, lang)}
                              onChange={(e) => setField(f.path, lang, e.target.value)}
                              className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none resize-y placeholder:text-neutral-700"
                            />
                          ) : (
                            <input
                              data-testid={`text-${f.path}-${lang}`}
                              value={overrides[`${f.path}.${lang}`] ?? ""}
                              placeholder={getDefault(f.path, lang)}
                              onChange={(e) => setField(f.path, lang, e.target.value)}
                              className="bg-transparent border border-[#262626] px-2 py-1.5 text-sm focus:border-[#ff4500] outline-none placeholder:text-neutral-700"
                            />
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
